// Capture home — list of bases the signed-in user can drill into
// (buildspec 20-ui §2, §3, §5.4a, §5.4b, §7).
//
// Data source: PowerSync `useBases` hook (Phase D-2 read-path
// migration). Reads from the local SQLite mirror via @powersync/react's
// useQuery. Sync-delivered changes to `bases`, `sites`, or `buildings`
// trigger an automatic re-render. Offline: the page renders from cached
// local data — the offline contract validated by the unit tests for
// `useBases`.
//
// Sorting follows buildspec §5.4a: display_order ASC, then name ASC.
// The SQL in `useBases` carries the contract so the UI iterates in
// order without a client-side sort step.

import { useEffect, useMemo, useState } from 'react'
import { Search as SearchIcon } from 'lucide-react'
import { useNavigate } from 'react-router'

import { Alert } from '../../ui/alert'
import { EmptyState } from '../../ui/empty-state'
import { Spinner } from '../../ui/spinner'
import { useAuth } from '../../app/auth-provider'
import { useBases, type UseBasesRow } from '../../hooks/powersync/use-bases'
import { filterByTokenPrefix } from '../../lib/token-prefix-search'
import { CaptureLayout } from '../components/capture-layout'

// 150ms debounce keeps typing responsive while skipping intermediate
// filter passes. Mirrors site-detail-page.tsx + base-detail-page.tsx.
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export function CaptureHomePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { bases, isLoading, error } = useBases()
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebouncedValue(searchQuery, 150)

  // Map useQuery's error to a user-safe string, with the same
  // null/undefined defensive check used by `useSites` consumers —
  // @powersync/react types `error` as `Error | undefined` but in some
  // states returns `null` at runtime.
  const errorMessage = error?.message ?? null

  const sortedBases = useMemo(() => {
    if (isLoading || errorMessage !== null) return null
    // useBases applies the §5.4a sort in SQL; we keep a defensive
    // client-side resort so a query change in the hook can't silently
    // produce a misordered grid.
    return [...bases].sort(compareBases)
  }, [bases, isLoading, errorMessage])

  const filteredBases = useMemo(() => {
    if (sortedBases === null) return null
    // Token-prefix match across name + code so "lin" surfaces "Linton"
    // but typing "rn" does NOT surface "Burnham" (mid-token substring).
    // PowerSync's auto-generated types treat text columns as nullable;
    // `filterByTokenPrefix` skips null/undefined fields, even though
    // the Drizzle schema is NOT NULL.
    return filterByTokenPrefix(sortedBases, debouncedQuery, (row) => [
      row.name,
      row.code,
    ])
  }, [sortedBases, debouncedQuery])

  const loading = isLoading

  return (
    <CaptureLayout>
      <section className="mx-auto w-full max-w-5xl space-y-6">
        {!loading && errorMessage === null && sortedBases !== null && user !== undefined ? (
          <CaptureHero user={user} />
        ) : (
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--color-cc-forest)]">
            Capture
          </h1>
        )}

        {loading ? <Spinner label="Loading bases" size="md" /> : null}

        {errorMessage !== null ? (
          <Alert variant="error" title="Could not load bases">
            {errorMessage}
          </Alert>
        ) : null}

        {!loading && errorMessage === null && sortedBases !== null ? (
          <div>
            {sortedBases.length === 0 ? (
              <EmptyState
                title="No bases configured"
                description="Ask an admin to import locations before starting capture."
              />
            ) : (
              <>
                <label className="mb-4 flex h-12 items-center gap-2 rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white px-3.5 shadow-sm">
                  <SearchIcon
                    aria-hidden="true"
                    className="h-4 w-4 text-[color:var(--color-cc-charcoal)]/60"
                  />
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search"
                    aria-label="Search bases"
                    // Suppress the browser's native search-clear glyph so the
                    // themed × button is the only clear affordance — without
                    // this WebKit/Chromium and Edge-legacy render a second
                    // grey X side-by-side with ours. Mirrors the pattern in
                    // add-asset-search-results.tsx + site-detail-page.tsx.
                    className="min-w-0 flex-1 bg-transparent text-sm text-[color:var(--color-cc-charcoal)] outline-none placeholder:text-[color:var(--color-cc-charcoal)]/45 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-ms-clear]:hidden"
                  />
                  {searchQuery ? (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                      className="grid h-6 w-6 place-items-center rounded-full bg-[color:var(--color-cc-cream-2)] text-base leading-none text-[color:var(--color-cc-charcoal)]"
                    >
                      ×
                    </button>
                  ) : null}
                </label>
                <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-forest)]/70">
                  <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)] align-middle" />
                  Bases
                </h2>
                {filteredBases !== null && filteredBases.length === 0 ? (
                  <EmptyState
                    title="No bases match your search"
                    description={
                      debouncedQuery
                        ? `No bases found for "${debouncedQuery}"`
                        : undefined
                    }
                  />
                ) : (
                  <BaseGrid
                    rows={filteredBases ?? sortedBases}
                    onSelect={(row) => navigate(`/capture/bases/${row.id}`)}
                  />
                )}
              </>
            )}
          </div>
        ) : null}
      </section>
    </CaptureLayout>
  )
}

interface CaptureHeroProps {
  user: { displayName: string; role: string }
}

// Signature Citycare hero: Forest panel with a Lime headline accent.
// Tagline echoes the brand promise so field staff land on something
// that feels Citycare-brand, not generic.
function CaptureHero({ user }: CaptureHeroProps) {
  const roleLabel = user.role === 'admin' ? 'admin' : 'field staff'

  return (
    <section className="overflow-hidden rounded-2xl bg-[color:var(--color-cc-forest)] px-6 py-8 text-[color:var(--color-cc-cream)] sm:px-10 sm:py-12">
      <span className="inline-flex items-center gap-2 rounded-full bg-[color:var(--color-cc-cream)]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-lime)]">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
        {roleLabel}
      </span>
      <h1 className="mt-4 font-display text-3xl font-semibold leading-tight text-[color:var(--color-cc-lime)] sm:text-5xl">Hi, {user.displayName}.</h1>
      <p className="mt-3 max-w-xl text-base text-[color:var(--color-cc-cream)]/70 sm:text-lg">
        Choose a base to begin.
      </p>
      <p className="mt-1 max-w-xl text-sm italic text-[color:var(--color-cc-cream)]/50">
        We discover. We deliver. We care.
      </p>
    </section>
  )
}

interface BaseGridProps {
  rows: readonly UseBasesRow[]
  onSelect: (row: UseBasesRow) => void
}

function BaseGrid({ rows, onSelect }: BaseGridProps) {
  return (
    <ul
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      aria-label="Bases"
    >
      {rows.map((row) => (
        <li key={row.id}>
          <BaseCard row={row} onSelect={() => onSelect(row)} />
        </li>
      ))}
    </ul>
  )
}

interface BaseCardProps {
  row: UseBasesRow
  onSelect: () => void
}

// Whole-card tap target (buildspec §9, AGENTS.md invariant #10).
// `min-h-16` = 64 px guarantees the ≥ 44 px minimum with headroom for
// text wrapping on narrow phones.
function BaseCard({ row, onSelect }: BaseCardProps) {
  const metadata = buildMetadata(row)
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Open base ${row.name}`}
      className="group flex min-h-16 w-full flex-col items-start gap-2 rounded-2xl border border-[color:var(--color-cc-forest)]/10 bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[color:var(--color-cc-forest)]/40 hover:shadow-lg active:translate-y-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-cc-lime)]"
    >
      <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[color:var(--color-cc-forest)]/60">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[color:var(--color-cc-lime)]" />
        {row.code}
      </span>
      <span className="font-display text-xl font-semibold leading-tight text-[color:var(--color-cc-forest)]">
        {row.name}
      </span>
      <span className="text-sm text-[color:var(--color-cc-charcoal)]/70">
        {metadata}
      </span>
    </button>
  )
}

function buildMetadata(row: UseBasesRow): string {
  const siteLabel = row.site_count === 1 ? 'site' : 'sites'
  const buildingLabel = row.building_count === 1 ? 'building' : 'buildings'
  return `${row.site_count} ${siteLabel} · ${row.building_count} ${buildingLabel}`
}

function compareBases(a: UseBasesRow, b: UseBasesRow): number {
  // PowerSync types text/integer columns as nullable — Drizzle says
  // NOT NULL but the SDK's generated types don't carry that constraint.
  // Defensive coalescing keeps the comparator safe at the type level.
  const aOrder = a.display_order ?? 0
  const bOrder = b.display_order ?? 0
  if (aOrder !== bOrder) return aOrder - bOrder
  return (a.name ?? '').localeCompare(b.name ?? '')
}
